import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ApiDataSource } from '../../utils/api-data-source';
import { UtilsService } from '../../shared/utils.service';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { TaskAnalysesRevisedModel } from './task-analyses-revised.model';
import { TaskAnalysesRevisedEditComponent } from './task-analyses-revised-edit/task-analyses-revised-edit.component';
import { TaskAnalysesRevisedViewComponent } from './task-analyses-revised-view/task-analyses-revised-view.component';
import { TaskAnalysesRevisedFilterComponent } from './task-analyses-revised-filter/task-analyses-revised-filter.component';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';

@Component({
  selector: 'app-task-analyses-revised',
  templateUrl: './task-analyses-revised.component.html',
  styleUrls: ['./task-analyses-revised.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TaskAnalysesRevisedComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new TaskAnalysesRevisedDataSource(this.app, this.api);

  displayedColumns = [
    'select',
    'id',
    'task_name',
    'work_procedure',
    // 'ppe',
    // 'work_permits',
    'created_by_user',
    'date_created',
    // 'date_created_UTC',
    'buttons'
  ];

  constructor(
    protected utils: UtilsService,
    private app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.dataSource.getData(true);
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'date_created_UTC') {
        this.dataSource.order_by = 'date_created';
      } else {
        this.dataSource.order_by = this.sort.active;
      }

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onAdd() {
    this.onEdit(new TaskAnalysesRevisedModel());
  }

  onEdit(ta_revised: TaskAnalysesRevisedModel) {
    // We should use this.utils.showComponentDialog() instead.
    this.dialog
      .open(TaskAnalysesRevisedEditComponent, {
        width: '1200px',
        data: ta_revised
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  onClone(ta_revised: TaskAnalysesRevisedModel) {
    const newTARevised = new TaskAnalysesRevisedModel();
    newTARevised.cloneFrom(ta_revised);
    this.onEdit(newTARevised);
  }

  onView(id: number) {
    this.utils.showComponentDialog(
      TaskAnalysesRevisedViewComponent,
      id,
      { width: '700px' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      }
    );
  }

  onRemoveSelected() {
    this.utils.showModal(
      'Archive Templates',
      `Are you sure you want to archive ${
        this.dataSource.selection.selected.length
      } templates?`,
      () => {
        this.remove(this.dataSource.selection.selected);
      }
    );
  }

  onRemove(ta_revised: TaskAnalysesRevisedModel) {
    this.utils.showModal(
      'Archive the Template',
      `Are you sure you want to archive "${
        ta_revised.task_name
      }" the template?`,
      () => {
        this.remove([ta_revised.id]);
      }
    );
  }

  private remove(ids: number[]) {
    this.api
      .makeRequest('delete', `v2/admin/task-analyses-revised/${ids.join(',')}`)
      .then((response) => {
        this.utils.showToast('The templates have been archived');
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  onFilter() {
    this.utils.showComponentDialog(
      TaskAnalysesRevisedFilterComponent,
      {
        selected_ppe_ids: this.dataSource.ppe_ids,
        selected_work_permit_ids: this.dataSource.work_permit_ids,
        filter_archived: this.dataSource.archived
      },
      {
        width: '768px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.ppe_ids = results.ppe_ids
            ? results.ppe_ids
            : this.dataSource.ppe_ids;
          this.dataSource.work_permit_ids = results.work_permit_ids
            ? results.work_permit_ids
            : this.dataSource.work_permit_ids;
          this.dataSource.archived = results.archived
            ? results.archived
            : this.dataSource.archived;
          this.dataSource.getData();
        }
      }
    );
  }

  onRestore(id: number) {
    this.utils.showModal(
      'Restore Template',
      'Are you sure you want to restore this template?',
      () => {
        this.api
          .makeRequest('put', `v2/admin/task-analyses-revised/${id}/restore`)
          .then((response) => {
            this.utils.showToast('The template was restored.');
            this.dataSource.selection.deselect(id);
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  onRestoreSelected() {
    this.utils.showModal(
      'Restore Selected Templates',
      'Are you sure you want to restore the selected templates?',
      () => {
        this.api
          .makeRequest(
            'put',
            `v2/admin/task-analyses-revised/${this.dataSource.selection.selected.join(
              ','
            )}/restore`
          )
          .then((response) => {
            this.utils.showToast('The selected templates were restored.');
            this.dataSource.selection.clear();
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }
}

export class TaskAnalysesRevisedDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'desc';
  searchBy = 'task_name';

  // Filter Variables
  ppe_ids = [];
  work_permit_ids = [];
  archived = 'false';

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/admin/task-analyses-revised', resetOffset, {
      searchBy: this.searchBy,
      ppe_ids: this.ppe_ids.length ? this.ppe_ids.join(',') : '',
      work_permit_ids: this.work_permit_ids.length
        ? this.work_permit_ids.join(',')
        : '',
      archived: this.archived
    });
  }
}
